import { accountMembershipActions } from '@mkp/account-membership/actions';
import { AccountMembership } from '@mkp/account-membership/data-access';
import { authActions, authOnboardingRecoveryActions, boAuthActions } from '@mkp/auth/actions';
import { legalEntityDetailsActions } from '@mkp/legal-entity/actions';
import { createEntityFeature, EntityFeatureState, LoadingState } from '@mkp/shared/util-state';
import { accountAggregateApiActions } from '@mkp/user-account-membership/actions';
import { Action, createReducer, on } from '@ngrx/store';

type State = EntityFeatureState<AccountMembership, { message: string }>;

const name = 'accountMembership';
const { featureKey, initialState, updaters, selectors } = createEntityFeature<
  State,
  'accountMembership'
>(name);

const _reducer = createReducer(
  { ...initialState },
  on(accountMembershipActions.reset, () => ({ ...updaters.reset() })),
  on(accountMembershipActions.loadAccountMembershipsSuccess, (state, { accountMemberships }) =>
    updaters.getListSuccess(state, { entities: accountMemberships })
  ),
  on(
    authActions.loadActiveAccountMembershipsSuccess,
    (state, { accountMemberships, totalCount }) => ({
      ...updaters.getListSuccess(state, { entities: accountMemberships }),
      selectedId: accountMemberships.length === 1 ? accountMemberships[0].id : null,
      totalCount,
    })
  ),
  on(
    accountMembershipActions.loadMoreAccountMembershipsSuccess,
    (state, { accountMemberships, filter, _links, totalCount }) => ({
      ...updaters.getPaginatedListSuccess(state, {
        entities: accountMemberships,
        filter,
        _links,
        totalCount,
      }),
    })
  ),
  on(
    accountAggregateApiActions.refreshOnPermissionCheckSuccess,
    accountAggregateApiActions.refreshOnRedeemInviteSuccess,
    (state, { accountMembershipsPayload: { accountMemberships, totalCount } }) => ({
      ...updaters.getListSuccess(state, { entities: accountMemberships }),
      selectedId: accountMemberships.length === 1 ? accountMemberships[0].id : null,
      totalCount,
    })
  ),
  on(
    accountAggregateApiActions.refreshOnPermissionCheckFailure,
    accountAggregateApiActions.refreshOnRedeemInviteFailure,
    (state) => ({
      ...updaters.getListSuccess(state, { entities: [] }),
      selectedId: null,
      totalCount: 0,
    })
  ),
  on(accountMembershipActions.createAccountMembershipSuccess, (state, { accountMembership }) => ({
    ...updaters.createSuccess(state, { entity: accountMembership }),
  })),
  on(accountMembershipActions.deleteAccountMembershipSuccess, (state, { accountMembershipId }) => ({
    ...updaters.deleteSuccess(state, { id: accountMembershipId }),
  })),
  on(accountMembershipActions.deleteSuccess, (state, { accountMembership }) => ({
    ...updaters.deleteSuccess(state, { id: accountMembership.id }),
  })),
  on(
    accountMembershipActions.updateReceiveNewApplicationEmailsSuccess,
    (state, { accountMembershipId, receiveNewApplicationEmails, _version }) =>
      updaters.updateSuccess(state, {
        updateEntity: {
          id: accountMembershipId,
          changes: { receiveNewApplicationEmails, _version },
        },
      })
  ),
  on(
    accountMembershipActions.createAccountMembershipFailure,
    accountMembershipActions.loadAccountMembershipsFailure,
    updaters.setError
  ),
  on(
    accountMembershipActions.loadAccountMemberships,
    accountMembershipActions.loadMoreAccountMemberships,
    accountMembershipActions.createAccountMembership,
    legalEntityDetailsActions.accountsLoadSuccess,
    authActions.loadAccountsAfterLoginSuccess,
    updaters.setLoading
  ),
  on(
    authActions.loadActiveAccountMembershipsFailed,
    authOnboardingRecoveryActions.onboardingFailedFromMembership,
    (state) => ({
      ...state,
      fetchState: LoadingState.LOADED,
      // Without this the count stays at its initial null, which reads as "not loaded yet"
      // rather than "loaded, and there are none". The sibling failure handler above already
      // settles it the same way.
      totalCount: 0,
    })
  ),
  on(authActions.workspaceSelected, (state, { accountMembershipId }) => ({
    ...state,
    selectedId: accountMembershipId,
  })),
  on(boAuthActions.workspaceUnselected, (state) => ({
    ...state,
    selectedId: null,
  }))
);

function reducer(state: State | undefined, action: Action): State {
  return _reducer(state, action);
}

export const accountMembershipFeature = { featureKey, initialState, reducer, selectors, name };
